import { Route, Routes } from 'react-router-dom';
import { PrivateRoute } from 'app/PrivateRoute';
import HasAccess from 'permissions/HasAccess';
import { Permission } from 'permissions/constants/Roles';
import ProductsAccess from 'permissions/ProductsAccess';
import { ProductLevel } from 'permissions/constants/ProductLevels';
import React from 'react';
import { SchoolRouteParams } from 'app/routes/SchoolRoutes';

const Agents = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/Agents'));
const AddAgent = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/Agents/AddAgent'));
const InstitutionUsers = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/InstitutionUsers'));
const AddUser = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/AddUser'));
const EditUser = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/EditUser'));
const EditSchool = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/EditSchool'));
const EditApplications = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/Application/Application'));
const Financial = React.lazy(() => import(/* webpackChunkName: 'settings' */ 'views/Settings/Financial/Financial'));
const ErrorPage = React.lazy(() => import(/* webpackChunkName: 'errorpage' */ 'views/Error/ErrorPage'));

export interface SchoolUserSettingsRouteParams extends SchoolRouteParams {
  id: string;
}

export interface SchoolInvoiceSettingsRouteParams extends SchoolRouteParams {
  id: string;
  duplicateId: string;
}

export const SchoolSettingsRoutes = () => (
  <Routes>
    <Route
      path="agents"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.ViewInstitutionAgents]} fallback={<ErrorPage />}>
            <Agents />
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="agents/add"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.InviteInstitutionAgents]} fallback={<ErrorPage />}>
            <AddAgent />
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="permissions"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.ViewInstitutionUsers]} fallback={<ErrorPage />}>
            <ProductsAccess disallow={[ProductLevel.EnrollerRecruit]}>
              <InstitutionUsers />
            </ProductsAccess>
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="permissions/add"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.AddInstitutionUsers]} fallback={<ErrorPage />}>
            <ProductsAccess disallow={[ProductLevel.EnrollerRecruit]}>
              <AddUser />
            </ProductsAccess>
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="permissions/edit/:id"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.EditInstitutionUsers]} fallback={<ErrorPage />}>
            <ProductsAccess disallow={[ProductLevel.EnrollerRecruit]}>
              <EditUser />
            </ProductsAccess>
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="school"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.ViewInstitutionSettings]} fallback={<ErrorPage />}>
            <EditSchool />
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="application"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.ViewApplicationSettings]} fallback={<ErrorPage />}>
            <EditApplications />
          </HasAccess>
        </PrivateRoute>
      }
    />
    <Route
      path="financial/*"
      element={
        <PrivateRoute>
          <HasAccess for="permission" name={[Permission.ViewFinancialSettings]} fallback={<ErrorPage />}>
            <ProductsAccess disallow={[ProductLevel.EnrollerRecruit]}>
              <Financial />
            </ProductsAccess>
          </HasAccess>
        </PrivateRoute>
      }
    />
  </Routes>
);
